import { DownloadManagerState, DownloadMode, DownloadManagerItemState, DownloadStatus } from '../containers/DownloadManager/reducer';
import { LoaderStep, LoaderStepName } from '../components/Loader';
import { DriveListState, DriveItemInfo } from '../containers/DriveItemList/reducer';
import { GalleryInfosState } from '../containers/Gallery/reducer';
import { Api } from '../typings/api';

export default class DownloadManagerUtils {

	public static getDownloadedItems(downloadManager: DownloadManagerState): number[] {
		const downloadList = downloadManager.items;
		return Object.keys(downloadList)
			.map(id => parseInt(id, 10))
			.filter(id => DownloadManagerUtils.isItemDownloaded(downloadManager, id));
	}

	public static getDownloadingItems(downloadManager: DownloadManagerState): number[] {
		const downloadList = downloadManager.items;
		return Object.keys(downloadList)
			.map(id => parseInt(id, 10))
			.filter(id => DownloadManagerUtils.isItemDownloading(downloadManager, id));
	}

	public static getItemLoaderStep(downloadManager: DownloadManagerState, driveItemId: number): LoaderStep | undefined {
		if (DownloadManagerUtils.isItemDownloading(downloadManager, driveItemId)) {
			if (DownloadManagerUtils.isDownloadingAsset(downloadManager, driveItemId)) {
				const progress = downloadManager.items[driveItemId] && downloadManager.items[driveItemId].progress ? downloadManager.items[driveItemId].progress : {};

				return {
					step: LoaderStepName.Downloading,
					progress
				};
			}

			if (DownloadManagerUtils.isAssetDownloaded(downloadManager, driveItemId) && !DownloadManagerUtils.isDependenciesDownloaded(downloadManager, driveItemId)) {
				if (downloadManager.items[driveItemId]) {
					const totalDependenciesDownloaded = DownloadManagerUtils.getTotalDependenciesDownloaded(downloadManager, driveItemId);

					return {
						step: LoaderStepName.DownloadingDependecies,
						dependenciesDownloaded: totalDependenciesDownloaded,
						dependenciesToDownload: downloadManager.items[driveItemId].dependencies.length,
					};
				}
			}
		}

		return;
	}

	public static getGalleryLoaderStep(downloadManager: DownloadManagerState, driveList: DriveListState, galleryId: number, isDownloadingAll: boolean): LoaderStep | undefined {
		if (isDownloadingAll) {
			return {
				step: LoaderStepName.DownloadingAll,
				itemsDownloaded: DownloadManagerUtils.getTotalItemsDownloaded(downloadManager, driveList),
				itemsToDownload: DownloadManagerUtils.getTotalItemsToDownload(downloadManager, driveList, galleryId),
			};
		}

		return;
	}

	public static isItemDownloading(downloadManager: DownloadManagerState, driveItemId: number) {
		return DownloadManagerUtils.isDownloadingAsset(downloadManager, driveItemId)
			|| DownloadManagerUtils.isDownloadingDependencies(downloadManager, driveItemId)
			|| (DownloadManagerUtils.isAssetDownloaded(downloadManager, driveItemId) && !DownloadManagerUtils.fetchedAllDependencies(downloadManager, driveItemId));
	}

	public static isRequiredItem(downloadManager: DownloadManagerState, driveItemId: number): boolean {
		return downloadManager.items[driveItemId]
			&& downloadManager.items[driveItemId].mode === DownloadMode.Required;
	}

	public static isRequestedItem(downloadManager: DownloadManagerState, driveItemId: number): boolean {
		return downloadManager.items[driveItemId]
			&& downloadManager.items[driveItemId].mode === DownloadMode.Requested;
	}

	public static isPersistentItem(item: DownloadManagerItemState): boolean {
		return item.mode === DownloadMode.Requested || item.mode === DownloadMode.Required;
	}

	public static isItemPersistentlyDownloaded(downloadManager: DownloadManagerState, driveItemId: number) {
		const driveItemInDownloadManager = downloadManager.items[driveItemId];
		return driveItemInDownloadManager
			&& DownloadManagerUtils.isPersistentItem(driveItemInDownloadManager)
			&& DownloadManagerUtils.isItemDownloaded(downloadManager, driveItemId);
	}

	public static isItemDownloaded(downloadManager: DownloadManagerState, driveItemId: number) {
		return DownloadManagerUtils.isAssetDownloaded(downloadManager, driveItemId)
			&& DownloadManagerUtils.isDependenciesDownloaded(downloadManager, driveItemId);
	}

	public static isLatestVersion = (serverVersion: number, downloadedVersion: number): boolean => downloadedVersion >= serverVersion;

	public static isDependencyOfPersistentDriveItem = (downloadManagerItems: { [k: number]: DownloadManagerItemState }, driveItemId: number, excludeArray: number[]): boolean => {
		const driveItemInDownloadManager = downloadManagerItems[driveItemId];
		const dependentsId = driveItemInDownloadManager.dependents;
		for (let i = 0; i < dependentsId.length; i++) {
			const dependentId = dependentsId[i];
			const isInExcludeArray = !excludeArray.includes(dependentId);
			if (isInExcludeArray) {
				const dependentInDownloadManager = downloadManagerItems[dependentId];
				if (DownloadManagerUtils.isPersistentItem(dependentInDownloadManager)) {
					return true;
				}

				if (DownloadManagerUtils.isDependencyOfPersistentDriveItem(downloadManagerItems, dependentId, [...excludeArray, ...dependentsId])) {
					return true;
				}
			}
		}
		return false;
	}

	public static hasItemsToDownload(downloadManager: DownloadManagerState, driveList: DriveListState, galleryInfos: GalleryInfosState): boolean {
		const { driveItems } = driveList;
		const driveItemsKeys = Object.keys(driveItems);
		let driveItemsIds: string[] = [];
		if ( driveItemsKeys.length > 0) {
			const galleryId = galleryInfos.config.applicationGallery;
			driveItemsIds = Object.keys(driveItems).filter(DownloadManagerUtils.filterDownloadableItems(driveItems, galleryId));
		}

		const hasItemsToDownload = driveItemsIds.some(key => {
			const itemId = parseInt(key, 10);
			const isItemNotDownloaded = !DownloadManagerUtils.isItemDownloaded(downloadManager, itemId);
			if (isItemNotDownloaded) {
				return true;
			}
			const downloadList = downloadManager.items;
			const needUpdate = !DownloadManagerUtils.isLatestVersion(driveItems[itemId].version, downloadList[itemId].version);

			return isItemNotDownloaded || needUpdate;
		});

		return hasItemsToDownload;
	}

	private static filterDownloadableItems(driveItems: { [k: number]: DriveItemInfo }, galleryId: number) {
		return (item) => {
			const isInGallery = driveItems[item].isInGallery;
			const isDownloadableType = ![
				Api.DriveItemType.Directory,
				Api.DriveItemType.Gallery,
				Api.DriveItemType.ExternalLink
			].includes(driveItems[item].type);
			const isNotGalleryItself = parseInt(item, 10) !== galleryId;
			return isInGallery && isDownloadableType && isNotGalleryItself;
		};
	}

	private static getTotalItemsDownloaded(downloadManager: DownloadManagerState, driveList: DriveListState): number {
		const { items } = downloadManager;
		const { driveItems } = driveList;
		const downloadedItemsIds = Object.keys(items).filter(item => {
			return DownloadManagerUtils.isItemPersistentlyDownloaded(downloadManager, parseInt(item, 10)) && driveItems[item].isInGallery;
		});

		return downloadedItemsIds.length;
	}

	private static getTotalItemsToDownload(downloadManager: DownloadManagerState, driveList: DriveListState, galleryId: number): number {
		const { driveItems } = driveList;
		const downloadableItemsIds = Object.keys(driveItems).filter(DownloadManagerUtils.filterDownloadableItems(driveItems, galleryId));
		return downloadableItemsIds.length;
	}

	private static getTotalDependenciesDownloaded(downloadManager: DownloadManagerState, driveItemId: number) {
		return downloadManager.items[driveItemId].dependencies.reduce(DownloadManagerUtils.getTotalDependenciesDownloadedReducer(downloadManager), 0);
	}

	private static getTotalDependenciesDownloadedReducer(downloadManager: DownloadManagerState) {
		return (total: number, dependencyId: number): number => {

			if (downloadManager.items[dependencyId] && downloadManager.items[dependencyId].status === DownloadStatus.NATIVE_DOWNLOADED) {
				return total + 1;
			}
			return total;
		};
	}

	private static isDownloadingAsset(downloadManager: DownloadManagerState, driveItemId: number) {
		return downloadManager.items[driveItemId]
			&& [DownloadStatus.INIT, DownloadStatus.GETTING_DEPENDENCIES, DownloadStatus.NATIVE_DOWNLOADING].includes(downloadManager.items[driveItemId].status);
	}

	private static isDownloadingDependencies(downloadManager: DownloadManagerState, driveItemId: number) {
		const itemFromDownloadManager = downloadManager.items[driveItemId];
		if (itemFromDownloadManager) {
			return itemFromDownloadManager.dependencies.some(dependencyId => DownloadManagerUtils.isDownloadingAsset(downloadManager, dependencyId));
		}
		return false;
	}

	private static isAssetDownloaded(downloadManager: DownloadManagerState, driveItemId: number) {
		return downloadManager.items[driveItemId] && downloadManager.items[driveItemId].status === DownloadStatus.NATIVE_DOWNLOADED;
	}

	private static isDependenciesDownloaded(downloadManager: DownloadManagerState, driveItemId: number) {
		const itemFromDownloadManager = downloadManager.items[driveItemId];
		if (itemFromDownloadManager) {
			return itemFromDownloadManager.dependencies.every(dependencyId => DownloadManagerUtils.isAssetDownloaded(downloadManager, dependencyId));
		}
		return false;
	}

	private static fetchedAllDependencies(downloadManager: DownloadManagerState, driveItemId: number) {
		const itemFromDownloadManager = downloadManager.items[driveItemId];
		return itemFromDownloadManager.dependencies.every(dependencyId => downloadManager.items[dependencyId] !== undefined);
	}
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/downloadManager.ts